import { useEffect } from "react";
import { motion } from "framer-motion";
import { PhoneCall, Globe, MessagesSquare, CalendarCheck, UserCheck, FileText, HeartPulse } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame, ComplianceNote } from "./_shared";
import AvaLiveDemo from "./AvaLiveDemo";

const CAN = [
  { icon: MessagesSquare, t: "Answer chat & calls", d: "On your website and your phone line — 24/7, no hold music." },
  { icon: Globe, t: "Speak their language", d: "Natural conversations across many languages, switching automatically." },
  { icon: UserCheck, t: "Qualify with your questions", d: "Industry-specific questions, urgency and sentiment detection, consistent routing." },
  { icon: CalendarCheck, t: "Book the appointment", d: "Real availability only — into your calendar, with reminders." },
  { icon: FileText, t: "Log everything", d: "Transcripts, summaries and CRM contacts created automatically." },
  { icon: HeartPulse, t: "Hand off to a human", d: "Transfers to your team the moment a person is the right answer." },
];

const INDUSTRIES = ["Restaurants", "Healthcare", "Hotels & travel", "Automotive", "Professional services", "Contractors", "Real estate"];

function CallMock() {
  const lines = [
    { who: "caller", text: "Hi, do you have any openings this Friday afternoon?" },
    { who: "ava", text: "We do — 2:30pm or 4:15pm are free Friday. Which suits you?" },
    { who: "caller", text: "4:15 works." },
    { who: "ava", text: "Booked for 4:15pm Friday. May I take a mobile number for the reminder?" },
  ];
  return (
    <BrowserFrame url="app.websitetech.pro/ava/live-call">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-emerald-600 mb-3"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Live call · English detected</div>
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className={`max-w-[85%] text-sm rounded-2xl px-3.5 py-2 ${l.who === "ava" ? "ml-auto bg-brand text-white rounded-br-sm" : "bg-slate-100 text-slate-700 rounded-bl-sm"}`}>{l.text}</div>
        ))}
      </div>
      <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-700 flex items-center gap-2"><CalendarCheck size={14} /> Appointment booked · Fri 4:15pm · CRM updated</div>
    </BrowserFrame>
  );
}

export default function Receptionist() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Ava — AI Receptionist & Qualification | AI Revenue Center"; }, []);
  return (
    <RevShell testid="rev-receptionist-page">
      <RevHero agent="Ava" eyebrow="AI Revenue Center™ · Receptionist & Qualification"
        title="Never miss an inquiry —" accent="on chat or the phone."
        sub="Ava answers every website chat and call, qualifies with your questions, books real appointments and hands off to your team — with a transcript and CRM record every time."
        visual={<CallMock />} />
      <ModuleBar />

      {/* Try it live */}
      <section className="py-16 bg-white border-b border-slate-100" data-testid="ava-try-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Try it now — real AI</span>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">Talk to Ava, then watch her book you in</h2>
            <p className="text-slate-500 mt-3">This is the actual assistant — ask about the Revenue Center, pricing or how it connects to your site. When you're ready, hit <strong>Book</strong> and Ava captures the appointment as a real lead in the CRM.</p>
            <ul className="mt-5 space-y-2 text-sm text-slate-600">
              {["Grounded only in approved WebsiteTech facts", "Qualifies and answers in natural language", "Books an appointment and confirms instantly"].map((x) => (
                <li key={x} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand shrink-0" /> {x}</li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-4">Demo booking shows a realistic confirmation and creates a genuine CRM lead — no live calendar is charged.</p>
            <a href="/receptionist" data-testid="build-own-receptionist-link" className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-lg bg-brand text-white text-sm font-semibold hover:opacity-90 transition-opacity">
              Build your own AI receptionist →
            </a>
          </div>
          <AvaLiveDemo />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHead eyebrow="What Ava does" title="A front desk that never sleeps" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {CAN.map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
              <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand"><c.icon size={18} /></div>
              <div className="font-heading font-bold text-slate-900 mt-3">{c.t}</div>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Built for your industry" title="Especially valuable when a missed call is a lost booking" />
          <div className="flex flex-wrap gap-2.5 mt-8">
            {INDUSTRIES.map((x) => (
              <span key={x} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700"><PhoneCall size={14} className="text-brand" /> {x}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <ComplianceNote>
          Ava only uses <strong>your approved knowledge</strong> and <strong>never fabricates</strong> availability, pricing or policy. Where laws require it, call-recording disclosure and AI-assistant identification are on by default.
        </ComplianceNote>
      </section>

      <RevCTA title="Put Ava on your phone and chat." sub="Start with a free audit — we'll show you how many inquiries are slipping through today." />
    </RevShell>
  );
}
